import type { GroundItemSnapshot } from '@dreamgame/shared';
import { signal } from '@preact/signals';

// Ground items storage keyed by networkId
const groundItemsMap = signal<Map<number, GroundItemSnapshot>>(new Map());

// PERFORMANCE: Cached array view, rebuilt only when signal reference changes
const _cachedGroundItems: GroundItemSnapshot[] = [];
let _cachedMapRef: Map<number, GroundItemSnapshot> | null = null;

// Get all ground items (for rendering)
export const getAllGroundItems = (): ReadonlyArray<GroundItemSnapshot> => {
  const map = groundItemsMap.value;
  if (map === _cachedMapRef) {
    return _cachedGroundItems;
  }
  _cachedMapRef = map;
  _cachedGroundItems.length = 0;
  for (const item of map.values()) {
    _cachedGroundItems.push(item);
  }
  return _cachedGroundItems;
};

// Get ground item by network ID
export const getGroundItem = ({
  networkId,
}: {
  networkId: number;
}): GroundItemSnapshot | undefined => {
  return groundItemsMap.value.get(networkId);
};

// Add or update a ground item
export const setGroundItem = ({ item }: { item: GroundItemSnapshot }): void => {
  const next = new Map(groundItemsMap.value);
  next.set(item.networkId, item);
  groundItemsMap.value = next;
};

// Add multiple ground items (for chunk loading)
export const setGroundItems = ({ items }: { items: ReadonlyArray<GroundItemSnapshot> }): void => {
  const next = new Map(groundItemsMap.value);
  for (const item of items) {
    next.set(item.networkId, item);
  }
  groundItemsMap.value = next;
};

// Remove a ground item
export const removeGroundItem = ({ networkId }: { networkId: number }): void => {
  const next = new Map(groundItemsMap.value);
  next.delete(networkId);
  groundItemsMap.value = next;
};

// Update ground item quantity
export const updateGroundItemQuantity = ({
  networkId,
  quantity,
}: {
  networkId: number;
  quantity: number;
}): void => {
  const item = groundItemsMap.value.get(networkId);
  if (!item) {
    return;
  }

  const next = new Map(groundItemsMap.value);
  next.set(networkId, { ...item, quantity });
  groundItemsMap.value = next;
};

// Clear all ground items (on disconnect)
export const clearGroundItems = (): void => {
  groundItemsMap.value = new Map();
};

// Get ground items at a specific tile
export const getGroundItemsAtTile = ({
  tileX,
  tileY,
}: {
  tileX: number;
  tileY: number;
}): ReadonlyArray<GroundItemSnapshot> => {
  const items: GroundItemSnapshot[] = [];
  for (const item of groundItemsMap.value.values()) {
    if (item.tileX === tileX && item.tileY === tileY) {
      items.push(item);
    }
  }
  return items;
};
